"use client";

import { useRouter } from "next/navigation";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import type { Job } from "@/lib/api/types";
import { formatShortDate } from "@/lib/date/format-short-date";
import {
  archiveJob as archiveJobApi,
  closeJob as closeJobApi,
  reopenJob as reopenJobApi,
} from "@/lib/api/generated";
import {
  isPermissionDenied,
  getPermissionErrorMessage,
} from "@/lib/permissions";
import {
  useJobListStore,
  useJobWizardStore,
} from "@/features/jobs/store/job-store";
import { showToast } from "@/components/ui/sonner";
import { ArchiveConfirmModal } from "@/components/ui/archive-confirm-modal";
import { JobActionsMenu } from "@/features/jobs/components/job-actions-menu";
import { useJobRowActions } from "@/features/jobs/hooks/use-job-row-actions";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { Icon } from "@/components/ui/icon";
import { Pagination } from "@/components/ui/pagination";

interface JobListCardsProps {
  jobs: Job[];
  total: number;
  isLoading: boolean;
  hasFilters?: boolean;
  onClearFilters?: () => void;
}

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, { bg: string; text: string; label: string }> = {
    active: {
      bg: "bg-[rgba(22,163,74,0.08)] dark:bg-[rgba(102,220,126,0.1)]",
      text: "text-[#15803d] dark:text-[#66dc7e]",
      label: "Active",
    },
    draft: {
      bg: "bg-[rgba(194,120,20,0.08)] dark:bg-[rgba(255,175,83,0.1)]",
      text: "text-[#b45309] dark:text-ai",
      label: "Draft",
    },
    closed: {
      bg: "bg-contrast/4",
      text: "text-contrast/50",
      label: "Closed",
    },
  };
  const s = styles[status] ?? styles.closed;
  return (
    <span
      className={`inline-flex items-center justify-center rounded-md p-0.5 text-xs font-medium ${s.bg} border border-border`}
    >
      <span className={`px-0.5 leading-4 ${s.text}`}>{s.label}</span>
    </span>
  );
}

function SkeletonCard({ index }: { index: number }) {
  return (
    <div
      className="bg-surface-elevated border-b border-border px-4 py-3.5 flex flex-col gap-3"
      style={{
        opacity: 0,
        animation: `skeleton-fade-in 0.4s ease-out ${index * 60}ms forwards`,
      }}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex flex-col gap-1.5 flex-1 min-w-0">
          <div className="h-4 bg-contrast/6 rounded w-3/4" />
          <div className="h-3 bg-contrast/6 rounded w-1/3" />
        </div>
        <div className="size-6 bg-contrast/6 rounded shrink-0" />
      </div>
      <div className="flex items-center gap-2">
        <div className="h-5 bg-contrast/6 rounded-md w-14" />
        <div className="h-3 bg-contrast/6 rounded w-20" />
        <div className="h-5 bg-contrast/6 rounded-md w-24" />
      </div>
    </div>
  );
}

function MobilePagination({ total }: { total: number }) {
  const { page, perPage, setPage } = useJobListStore();
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  if (totalPages <= 1) return null;

  return (
    <Pagination.Root
      page={page}
      totalPages={totalPages}
      onPageChange={setPage}
      variant="list"
    >
      <div className="flex items-center justify-between px-4 py-3 border-t border-border bg-surface-elevated">
        <Pagination.Prev className="h-8 gap-1.5 rounded-lg border-border px-3 text-sm" />
        <span className="text-sm text-contrast/50">
          {page} / {totalPages}
        </span>
        <Pagination.Next className="h-8 gap-1.5 rounded-lg border-border px-3 text-sm" />
      </div>
    </Pagination.Root>
  );
}

export function JobListCards({
  jobs,
  total,
  isLoading,
  hasFilters,
  onClearFilters,
}: JobListCardsProps) {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { resumeWizard } = useJobWizardStore();
  const { cloneJob, cloningJobId } = useJobRowActions();
  const [archiveTarget, setArchiveTarget] = useState<Job | null>(null);

  const handleJobClick = (job: Job) => {
    if (job.status === "draft") {
      const nextStep = Math.min((job.wizard_step || 1) + 1, 4) as 1 | 2 | 3 | 4;
      const apiStages = job.stages?.length
        ? [...job.stages]
            .sort((a, b) => a.position - b.position)
            .map((s) => ({
              id: s.id,
              name: s.name,
              color: s.color,
              description: s.description ?? "",
              is_system: s.is_system,
            }))
        : undefined;
      resumeWizard(
        job.id,
        nextStep,
        {
          title: job.title,
          description: job.description || "",
          managerId: job.job_manager_id,
          ...(apiStages ? { stages: apiStages } : {}),
        },
        job.project_id ?? null,
      );
    } else {
      router.push(`/jobs/${job.id}`);
    }
  };

  const closeJob = useMutation({
    mutationFn: (jobId: string) => closeJobApi(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      showToast.success("Job closed");
    },
    onError: (err: Error) => {
      if (isPermissionDenied(err))
        showToast.error("Permission denied", {
          description: getPermissionErrorMessage(err),
        });
      else showToast.error("Failed to close job", { description: err.message });
    },
  });

  const reopenJob = useMutation({
    mutationFn: (jobId: string) => reopenJobApi(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      showToast.success("Job reopened");
    },
    onError: (err: Error) => {
      if (isPermissionDenied(err))
        showToast.error("Permission denied", {
          description: getPermissionErrorMessage(err),
        });
      else
        showToast.error("Failed to reopen job", { description: err.message });
    },
  });

  const archiveJob = useMutation({
    mutationFn: (jobId: string) => archiveJobApi(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["jobs"] });
      setArchiveTarget(null);
      showToast.success("Job archived");
    },
    onError: (err: Error) => {
      if (isPermissionDenied(err))
        showToast.error("Permission denied", {
          description: getPermissionErrorMessage(err),
        });
      else
        showToast.error("Failed to archive job", { description: err.message });
    },
  });

  if (isLoading) {
    return (
      <div className="flex-1 flex flex-col overflow-y-auto bg-surface-primary shimmer shimmer-bg text-foreground">
        {[...Array(8)].map((_, i) => (
          <SkeletonCard key={i} index={i} />
        ))}
      </div>
    );
  }

  if (jobs.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6 py-12 bg-surface-primary">
        <div
          aria-hidden="true"
          className="flex size-14 items-center justify-center rounded-xl border border-border bg-surface-inset backdrop-blur-sm"
        >
          <Icon name="IconBag" size={24} className="text-heading opacity-30" />
        </div>
        <div className="flex flex-col items-center gap-1.5">
          <h3 className="text-base font-medium text-heading">
            No matching jobs
          </h3>
          <p className="text-sm text-contrast/50 text-center">
            Try adjusting your filters or search query.
          </p>
        </div>
        {hasFilters && onClearFilters && (
          <Button
            type="button"
            onClick={onClearFilters}
            variant="secondary"
            size="sm"
            className="h-8 px-3"
          >
            Clear all filters
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 flex flex-col overflow-y-auto bg-surface-primary">
        {jobs.map((job) => (
          <div
            key={job.id}
            onClick={() => handleJobClick(job)}
            className="dark:bg-surface-elevated border-b border-border px-4 py-3.5 flex flex-col gap-2.5 cursor-pointer active:bg-contrast/2 transition-colors"
          >
            <div className="flex items-start justify-between gap-2">
              <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <Icon
                    name="IconBag"
                    size={14}
                    className="text-heading opacity-50 shrink-0"
                  />
                  <span className="text-sm font-medium text-heading truncate">
                    {job.title}
                  </span>
                </div>
                <span className="text-xs text-contrast/50 pl-[22px]">
                  {job.candidate_count}{" "}
                  {job.candidate_count === 1 ? "Profile" : "Profiles"}
                </span>
              </div>

              <div onClick={(e) => e.stopPropagation()}>
                <JobActionsMenu
                  job={job}
                  onView={() => handleJobClick(job)}
                  onClone={() => void cloneJob(job)}
                  cloneDisabled={cloningJobId === job.id}
                  onClose={() => closeJob.mutate(job.id)}
                  onReopen={() => reopenJob.mutate(job.id)}
                  onArchive={() => setArchiveTarget(job)}
                />
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <StatusBadge status={job.status} />

              <span className="text-xs text-contrast/40">
                {formatShortDate(job.created_at)}
              </span>

              {job.job_manager ? (
                <div className="inline-flex items-center gap-0 bg-contrast/4 border border-contrast/5 rounded-md p-0.5 overflow-clip">
                  <div className="flex items-center px-0.5">
                    <Avatar className="size-[13px]">
                      <AvatarImage src={job.job_manager.avatar_url} alt="" />
                      <AvatarFallback>
                        <Icon
                          name="IconPeopleCircle"
                          size={8}
                          className="text-heading opacity-20"
                        />
                      </AvatarFallback>
                    </Avatar>
                  </div>
                  <span className="text-xs font-medium leading-4 text-contrast/75 px-0.5">
                    {[job.job_manager.first_name, job.job_manager.last_name]
                      .filter(Boolean)
                      .join(" ") || job.job_manager.email}
                  </span>
                </div>
              ) : (
                <span className="text-xs text-contrast/40">Unassigned</span>
              )}
            </div>
          </div>
        ))}

        <MobilePagination total={total} />
      </div>

      <ArchiveConfirmModal
        open={!!archiveTarget}
        onClose={() => setArchiveTarget(null)}
        onConfirm={() => {
          if (archiveTarget) archiveJob.mutate(archiveTarget.id);
        }}
        entityType="job"
        entityName={archiveTarget?.title ?? ""}
        loading={archiveJob.isPending}
        description="Archiving this job removes it from your active job list and pipeline views."
      />
    </>
  );
}
